import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { BUILD_VERSION } from '../../../environments/version';
import { ToasterService } from '../../shared/toasts/services/toasts.service';

const SERVICE_WORKER_URL = `/service-worker.js?v=${encodeURIComponent(
  BUILD_VERSION,
)}`;
const RELOAD_STORAGE_KEY = `secadm-sw-reloaded-${BUILD_VERSION}`;

interface ServiceWorkerMessage {
  type?: string;
  version?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AppUpdateService {
  private readonly toasterService = inject(ToasterService);

  private initialized = false;
  private reloading = false;

  init(): void {
    if (
      this.initialized ||
      !environment.production ||
      !this.canUseServiceWorker()
    ) {
      return;
    }

    this.initialized = true;
    this.listenForMessages();
    this.listenForControllerChanges(!!navigator.serviceWorker.controller);

    if (document.readyState === 'complete') {
      void this.register();
      return;
    }

    window.addEventListener('load', () => void this.register(), {
      once: true,
    });
  }

  private async register(): Promise<void> {
    try {
      const registration = await navigator.serviceWorker.register(
        SERVICE_WORKER_URL,
        {
          updateViaCache: 'none',
        },
      );

      this.watchForUpdates(registration);
      window.addEventListener('focus', () => void registration.update());

      if (registration.waiting && navigator.serviceWorker.controller) {
        this.applyWaitingWorker(registration);
      }

      await registration.update();
    } catch (error) {
      console.error('[ServiceWorker] Registration failed', error);
    }
  }

  private watchForUpdates(registration: ServiceWorkerRegistration): void {
    registration.addEventListener('updatefound', () => {
      const installingWorker = registration.installing;

      if (!installingWorker) {
        return;
      }

      installingWorker.addEventListener('statechange', () => {
        if (
          installingWorker.state === 'installed' &&
          navigator.serviceWorker.controller
        ) {
          this.applyWaitingWorker(registration);
        }
      });
    });
  }

  private applyWaitingWorker(registration: ServiceWorkerRegistration): void {
    this.toasterService.info(
      'Nueva version disponible',
      'Actualizando aplicacion...',
    );

    registration.waiting?.postMessage({
      type: 'SKIP_WAITING',
    });
  }

  private listenForControllerChanges(hadController: boolean): void {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (
        !hadController ||
        this.reloading ||
        sessionStorage.getItem(RELOAD_STORAGE_KEY)
      ) {
        return;
      }

      this.reloading = true;
      sessionStorage.setItem(RELOAD_STORAGE_KEY, 'true');
      window.location.reload();
    });
  }

  private listenForMessages(): void {
    navigator.serviceWorker.addEventListener(
      'message',
      (event: MessageEvent<ServiceWorkerMessage>) => {
        if (event.data?.type === 'SW_ACTIVATED') {
          console.info(
            `[ServiceWorker] Active version: ${event.data.version}`,
          );
        }
      },
    );
  }

  private canUseServiceWorker(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      'serviceWorker' in navigator &&
      typeof window !== 'undefined' &&
      typeof document !== 'undefined'
    );
  }
}
